import { useEffect, useMemo, useRef, useState } from "react";
import TrackTile from "../common/TrackSelectionTile";
import SelectorTileGrid from "../common/SelectorTileGrid";
import miniTrackImage from "@/assets/images/miniTrack_.jpeg";
import mainTrackImage from "@/assets/images/main_track_.jpg";
import { ROUTES, TRACKS } from "@/consts";
import { TTrackTypes } from "@/types";
import { useTrackContext } from "@/state/TrackContext";
import { useUrlParams } from "@/hooks/useUrlParams";
import { useSelectorIdleHint } from "@/hooks/useSelectorIdleHint";

import BottomSummary from "../BottomSummary";
import { DynamicFullHeightContainer } from "../common/FullHeightContainer";
import { fetchAndDispatchMetaObjectConfig } from "@/shared/utils/metaobjectOperations";
import { BookingAnalytics } from "@/shared/utils/bookingAnalytics";
import { usePathwayStyling } from "@/hooks/usePathwayStyling";
import { AGE_GROUP_CONFIG, type AgeGroupKey } from "@/shared/ageGroupSelectionConfig";

// The tiles divide up whatever height the container has; this is only the floor
// they may shrink to on short viewports. It sits below the 120px design height
// because four 120px tiles plus the heading overflow a phone-sized container, and
// the scroll container turns that excess into a scrollbar on a screen that is
// meant to fit the viewport.
const MIN_TILE_HEIGHT = 88;
// The grid's own vertical spacing on mobile: tw-my-[5px] top and bottom plus the
// three tw-gap-[5px] gaps between four single-column rows.
const MOBILE_GRID_SPACING = 25;
// Reserved for the fixed bottom summary. The experience-variant footer is 20px of
// wrapper padding, 8px of header padding and a 50px action pill when the cart is
// empty, and grows to roughly this once the cart bar appears.
const MOBILE_FOOTER_HEIGHT = 88;
const DESKTOP_FOOTER_HEIGHT = 86;
const MOBILE_BREAKPOINT = 767;
// Width at which the row can hold four 280px tiles, their 50px gaps and the glow room
// either side (4 x 280 + 3 x 50 + 2 x 36 = 1342; 1380 is the theme's desktop
// breakpoint). Below it the tiles fill their column instead of keeping the fixed
// width and spilling out of it, which would push the outermost tile back into the
// glow room the grid just reserved. See .selector-tile-grid in App.css.
const FIXED_TILE_WIDTH_MIN = 1380;

// vercel deployment test

const RacingTrackSelection = () => {
  const { navigateWithParams } = useUrlParams();
  const { getHintIndex, markEngaged } = useSelectorIdleHint();
  const {
    dispatch,
    productRenderedLocation,
    trackSelectionOption: { title },
    windowWidth,
    metaObjectTrackSelectionConfig,
    appHostedAt,
  } = useTrackContext();
  const { titleStyle, subtitleStyle, visibility } = usePathwayStyling("ageGroupSelection");

  const { mainTrack, intermediateTrack, miniTrack, juniorTrack, ageGroupSelectionScreen } = metaObjectTrackSelectionConfig;
  const showMain = Boolean(mainTrack) && visibility.mainTrack !== false;
  const showIntermediate = Boolean(intermediateTrack) && visibility.intermediateTrack !== false;
  const showJunior = Boolean(juniorTrack) && visibility.juniorTrack !== false;
  const showMini = Boolean(miniTrack) && visibility.miniTrack !== false;

  useEffect(() => {
    const initializeMetaObjectConfig = async () => {
      try {
        if (mainTrack === undefined || miniTrack === undefined || juniorTrack === undefined) {
          await fetchAndDispatchMetaObjectConfig(dispatch);
        }
      } catch (error) {
        console.error("Failed to initialize metaobject configuration:", error);
      }
    };

    initializeMetaObjectConfig();
  }, [dispatch, juniorTrack, mainTrack, miniTrack]);

  const [selectedTrack, setSelectedTrack] = useState<TTrackTypes>(title as TTrackTypes);

  const [containerHeightPx, setContainerHeightPx] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const [gridTopOffsetPx, setGridTopOffsetPx] = useState(0);

  // Experiment p4-v2: single-tap commit. Tapping an age tile selects that age group and
  // advances immediately — there is no multi-selection set and no Next button.
  const selectAgeGroup = (key: AgeGroupKey) => {
    markEngaged();
    const config = AGE_GROUP_CONFIG[key];

    BookingAnalytics.ageGroupSelected(key, config.trackType);

    setSelectedTrack(config.trackType);

    dispatch({
      type: "SET_AGE_GROUP",
      payload: {
        selectedTrack: config.trackType,
        ageGroup: config.ageGroup,
      },
    });

    dispatch({
      type: "SET_SELECTED_TRACK",
      payload: {
        selectedTrackTitle: config.trackType,
        trackBanner: key === "MINI_TRACK" ? miniTrackImage : mainTrackImage,
      },
    });

    const navParams = key === "15+" || key === "10-14" ? { ageGroup: config.ageGroup || undefined } : undefined;
    navigateWithParams(config.route, navParams);
  };

  const handleTrackSelection = (track: TTrackTypes, imageSrc: string) => {
    setSelectedTrack(track);
    dispatch({
      type: "SET_SELECTED_TRACK",
      payload: {
        selectedTrackTitle: track,
        trackBanner: imageSrc,
      },
    });

    let route = "";
    switch (track) {
      case TRACKS.JUNIOR_TRACK:
        route = ROUTES.JUNIOR_TRACK.SEATS_RACES;
        break;
      case TRACKS.MINI_TRACK:
        route = ROUTES.MINI_TRACK.SEATS_RACES;
    }

    if (route) {
      return navigateWithParams(route);
    }
  };

  // Measure how much vertical space the heading/subtitle area pushes the grid down by
  // (its offsetTop inside the relatively-positioned DynamicFullHeightContainer includes
  // the heading element and its margins). We use this so the tiles fit without scroll.
  useEffect(() => {
    const measureGridOffset = () => {
      if (gridRef.current) {
        setGridTopOffsetPx(gridRef.current.offsetTop);
      }
    };
    measureGridOffset();
    window.addEventListener("resize", measureGridOffset);
    return () => window.removeEventListener("resize", measureGridOffset);
  }, [ageGroupSelectionScreen?.title, ageGroupSelectionScreen?.subtitle, containerHeightPx, windowWidth]);

  const tileHeight = useMemo(() => {
    const heightAfterSpacing = containerHeightPx - gridTopOffsetPx - MOBILE_GRID_SPACING;
    return Math.max(MIN_TILE_HEIGHT, heightAfterSpacing / 4);
  }, [containerHeightPx, gridTopOffsetPx]);

  const footerHeight = useMemo(() => {
    return windowWidth <= MOBILE_BREAKPOINT ? MOBILE_FOOTER_HEIGHT : DESKTOP_FOOTER_HEIGHT;
  }, [windowWidth]);

  return (
    <>
      <div className={`${productRenderedLocation === "external" ? "tw-container tw-mx-auto" : "container mx-auto"} tw-h-full tw-min-h-0`}>
        {appHostedAt && appHostedAt === "bosshunting" ? (
          <>
            <div className="tw-flex tw-justify-center tw-items-center">
              {mainTrack && title === "MAIN_TRACK" && (
                <TrackTile
                  onDoubleClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage)}
                  onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage)}
                  isSelected={selectedTrack === TRACKS.MAIN_TRACK}
                  title={mainTrack.title}
                  titleFieldKey="banner_main_track.title"
                  description={mainTrack.description}
                  descriptionFieldKey="banner_main_track.description"
                  image={mainTrackImage}
                  videoBannerUrl={mainTrack.video_banner_url}
                  mobileVideoBannerUrl={mainTrack.mobile_video_banner_url}
                />
              )}

              {miniTrack && title === "MINI_TRACK" && (
                <TrackTile
                  onDoubleClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage)}
                  onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage)}
                  isSelected={selectedTrack === TRACKS.MINI_TRACK}
                  title={miniTrack.title}
                  titleFieldKey="banner_mini_track.title"
                  description={miniTrack.description}
                  descriptionFieldKey="banner_mini_track.description"
                  image={miniTrackImage}
                  videoBannerUrl={miniTrack.video_banner_url}
                  mobileVideoBannerUrl={miniTrack.mobile_video_banner_url}
                />
              )}
              {juniorTrack && title === "JUNIOR_TRACK" && (
                <TrackTile
                  onDoubleClick={() => handleTrackSelection("JUNIOR_TRACK", mainTrackImage)}
                  onClick={() => handleTrackSelection("JUNIOR_TRACK", mainTrackImage)}
                  isSelected={selectedTrack === TRACKS.JUNIOR_TRACK}
                  title={juniorTrack.title}
                  titleFieldKey="banner_junior_track.title"
                  description={juniorTrack.description}
                  descriptionFieldKey="banner_junior_track.description"
                  image={mainTrackImage}
                  videoBannerUrl={juniorTrack.video_banner_url}
                  mobileVideoBannerUrl={juniorTrack.mobile_video_banner_url}
                />
              )}
            </div>

            <div className="tw-bg-[#2F1160] tw-w-fit tw-mx-auto tw-py-[8px] tw-px-[16px] tw-rounded-[8px] tw-mb-[15px] tw-mt-[30px]">
              <span className="tw-text-[white] tw-font-[700] tw-text-center tw-flex">
                {title === "MAIN_TRACK" ? "Main Track" : title === "MINI_TRACK" ? "Mini Track" : "Junior Track"} Selected{" "}
              </span>
            </div>

            <div className={`${windowWidth >= 640 ? "tw-flex tw-justify-center tw-items-center" : ""} tw-gap-[10px] tw-my-[20px]`}>
              <button
                type="button"
                onClick={() => handleTrackSelection("MAIN_TRACK", mainTrackImage)}
                style={{ width: "200px" }}
                className={`tw-rounded-[100px] tw-block ${windowWidth >= 640 ? "tw-mx-[0]" : "tw-mx-auto"} tw-text-center tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                  title === "MAIN_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                }`}
              >
                Main Track
              </button>
              <button
                type="button"
                onClick={() => handleTrackSelection("MINI_TRACK", miniTrackImage)}
                style={{ width: "200px" }}
                className={`tw-rounded-[100px] tw-block ${
                  windowWidth >= 640 ? "tw-mx-[0] tw-my-[0]" : "tw-mx-auto"
                } tw-text-center tw-my-[10px] tw-py-[8px] tw-px-[20px] tw-text-[18px] tw-font-bold ${
                  title === "MINI_TRACK" ? "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)] tw-text-white" : "tw-bg-[#393b41] tw-text-[#777777]"
                }`}
              >
                Mini Track
              </button>
            </div>
          </>
        ) : (
          <>
            <DynamicFullHeightContainer footerHeight={footerHeight} containerHeightPx={containerHeightPx} setContainerHeightPx={setContainerHeightPx}>
              {/* Flex-column at every breakpoint (mirrors the root experience selector):
                  flex items' margins never collapse, so the heading's top margin stays
                  inside the h-full wrapper instead of pushing it past the container
                  bottom (a phantom 16px scroll on mobile). The grid is flex-1 with
                  bounded `fr` rows from lg up, so `desktopFill` tiles shrink to fit
                  instead of overflowing into the container's internal scroll. */}
              <div className="tw-flex tw-flex-col tw-h-full">
              {(ageGroupSelectionScreen?.title || ageGroupSelectionScreen?.subtitle) && (
                <div className="tw-text-center tw-mt-[16px] lg:tw-mt-[40px] tw-mb-[8px] lg:tw-mb-[16px] sm:tw-px-4 tw-flex-shrink-0">
                  {ageGroupSelectionScreen.title && (
                    <h2
                      data-hlc-field="banner_general.ag_title"
                      className="tw-text-white tw-text-[18px] sm:tw-text-[22px] lg:tw-text-[26px] tw-font-extrabold tw-mb-2"
                      style={titleStyle}
                    >
                      {ageGroupSelectionScreen.title}
                    </h2>
                  )}
                  {ageGroupSelectionScreen.subtitle && (
                    <p
                      data-hlc-field="banner_general.ag_subtitle"
                      className="tw-text-white max-480:tw-text-[12px] sm:tw-text-[14px] lg:tw-text-[24px] tw-font-[500] lg:tw-font-[800]"
                      style={subtitleStyle}
                    >
                      {ageGroupSelectionScreen.subtitle}
                    </p>
                  )}
                </div>
              )}
              <SelectorTileGrid
                ref={gridRef}
                className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-[5px] lg:tw-gap-[50px] tw-my-[5px] lg:tw-mt-[20px] lg:tw-mb-[40px] lg:tw-flex-1 lg:tw-min-h-0 lg:tw-auto-rows-fr"
              >
                {showMain && mainTrack && (
                  <TrackTile
                    onClick={() => selectAgeGroup("15+")}
                    isSelected={false}
                    title={"Ages 15+"}
                    description={mainTrack.description}
                    image={mainTrackImage}
                    videoBannerUrl={mainTrack.video_banner_url}
                    mobileVideoBannerUrl={mainTrack.mobile_video_banner_url}
                    descriptionFieldKey="banner_main_track.description"
                    badge="Adult Track"
                    heightPx={tileHeight}
                    fill={windowWidth < FIXED_TILE_WIDTH_MIN}
                    desktopFill
                    waveIndex={getHintIndex(0)}
                    onEngage={markEngaged}
                  />
                )}

                {showIntermediate && intermediateTrack && (
                  <TrackTile
                    onClick={() => selectAgeGroup("10-14")}
                    isSelected={false}
                    title="Ages 10-14"
                    description={intermediateTrack.description}
                    image={mainTrackImage}
                    videoBannerUrl={intermediateTrack.video_banner_url}
                    mobileVideoBannerUrl={intermediateTrack.mobile_video_banner_url}
                    descriptionFieldKey="banner_intermediate_track.description"
                    badge="Intermediate Track"
                    heightPx={tileHeight}
                    fill={windowWidth < FIXED_TILE_WIDTH_MIN}
                    desktopFill
                    waveIndex={getHintIndex(1)}
                    onEngage={markEngaged}
                  />
                )}

                {showJunior && juniorTrack && (
                  <TrackTile
                    onClick={() => selectAgeGroup("JUNIOR_TRACK")}
                    isSelected={false}
                    title="Ages 7-9"
                    description={juniorTrack.description}
                    image={mainTrackImage}
                    videoBannerUrl={juniorTrack.video_banner_url}
                    mobileVideoBannerUrl={juniorTrack.mobile_video_banner_url}
                    descriptionFieldKey="banner_junior_track.description"
                    badge="Junior Track"
                    heightPx={tileHeight}
                    fill={windowWidth < FIXED_TILE_WIDTH_MIN}
                    desktopFill
                    waveIndex={getHintIndex(2)}
                    onEngage={markEngaged}
                  />
                )}

                {showMini && miniTrack && (
                  <TrackTile
                    onClick={() => selectAgeGroup("MINI_TRACK")}
                    isSelected={false}
                    title="Ages 3-6"
                    description=""
                    image={miniTrackImage}
                    videoBannerUrl={miniTrack.video_banner_url}
                    mobileVideoBannerUrl={miniTrack.mobile_video_banner_url}
                    descriptionFieldKey="banner_mini_track.description"
                    badge="Mini Track"
                    heightPx={tileHeight}
                    fill={windowWidth < FIXED_TILE_WIDTH_MIN}
                    desktopFill
                    waveIndex={getHintIndex(3)}
                    onEngage={markEngaged}
                  />
                )}
              </SelectorTileGrid>
              </div>
            </DynamicFullHeightContainer>
          </>
        )}
      </div>
      {/* No Next button — a single age-tile tap advances immediately, so nextInfo is
          omitted. The footer is still registered so Back / Checkout (with cart items) work. */}
      <BottomSummary
        previousInfo={{ pageName: "Experience", pagePath: ROUTES.INDEX, isActive: true }}
        mobileNavVariant="experience"
        actionAlignRef={gridRef}
      />
    </>
  );
};

export default RacingTrackSelection;
